import { useState, useEffect } from 'react';
import { FolderOpen, Trash2, Loader2, Calendar, MapPin } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { listScenarios, deleteScenario } from '../../lib/scenarioStorage';

export const LoadScenarioModal = ({ open, onOpenChange, onLoad }) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (open) {
      fetchScenarios();
    }
  }, [open]);

  const fetchScenarios = () => {
    setLoading(true);
    setScenarios(listScenarios());
    setLoading(false);
  };

  const handleDelete = (scenarioId, e) => {
    e.stopPropagation();
    setDeleting(scenarioId);
    try {
      deleteScenario(scenarioId);
      setScenarios(prev => prev.filter(s => s.id !== scenarioId));
      toast.success('Szenario gelöscht');
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleting(null);
    }
  };

  const handleLoad = (scenario) => {
    onLoad(scenario);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="load-scenario-modal">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2 text-lunefire-charcoal dark:text-white">
            <FolderOpen className="w-5 h-5 text-lunefire-red" />
            Szenario laden
          </DialogTitle>
          <DialogDescription className="font-body">
            Gespeicherte Szenarien in diesem Browser. Szenarien von anderen über „Szenario importieren“ laden.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : scenarios.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">Keine Szenarien gespeichert</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Erstellen Sie ein Szenario und speichern Sie es.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  onClick={() => handleLoad(scenario)}
                  className="p-4 rounded-md border border-border hover:border-lunefire-red/50 hover:bg-lunefire-red/5 cursor-pointer transition-all group"
                  data-testid={`scenario-item-${scenario.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold truncate">{scenario.name}</h4>
                      {scenario.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {scenario.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(scenario.updated_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {scenario.elements?.length || 0} Elemente
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 touch:h-10 touch:w-10 opacity-0 group-hover:opacity-100 touch:opacity-100 focus-visible:opacity-100 transition-opacity text-lunefire-red hover:text-lunefire-red hover:bg-lunefire-red/10"
                      onClick={(e) => handleDelete(scenario.id, e)}
                      disabled={deleting === scenario.id}
                      data-testid={`delete-scenario-${scenario.id}`}
                    >
                      {deleting === scenario.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default LoadScenarioModal;
